import { createFileRoute, useNavigate } from "@tanstack/react-router";
import {
  Facebook,
  Instagram,
  Check,
  ArrowRight,
  Target,
  Layout,
  Code2,
  Smartphone,
  Gauge,
  MousePointerClick,
  Search,
  BarChart3,
  Rocket,
  AlertTriangle,
  MessageSquareOff,
  Ban,
} from "lucide-react";
import { FOOTER_SOCIAL_LINKS } from "@/components/ui/SocialLinks";
import { HeroNav } from "@/components/ui/hero/HeroNav";

import { HeroSocialProof } from "@/components/ui/hero/HeroSocialProof";
import { Footer } from "@/components/ui/footer";
import { ContactSection } from "@/components/sections/ContactSection";
import { WhyUsSection } from "@/components/sections/WhyUsSection";
import { AboutSection } from "@/components/sections/AboutSection";
import { ProblemSection } from "@/components/sections/ProblemSection";
import { SelectedWork } from "@/components/portfolio/SelectedWork";
import { projectsQueryOptions } from "@/lib/portfolio";
import { TestimonialFeatureSection } from "@/components/sections/TestimonialFeatureSection";
import { PricingSection } from "@/components/sections/PricingSection";
import { FaqSection } from "@/components/sections/FaqSection";
import {
  leftItems as faqLeft,
  rightItems as faqRight,
} from "@/components/sections/FaqSection.data";
import { cn } from "@/lib/utils";
import { scrollToAnchor } from "@/lib/scroll-to-anchor";
import logo from "@/assets/mind-media-logo.svg";

export const Route = createFileRoute("/websites")({
  head: () => ({
    meta: [
      { title: "Website Design & Development — Mind Media" },
      {
        name: "description",
        content:
          "Strategy-led website design and development. Fast, modern sites built to turn visitors into leads and sales. You see it before you pay anything.",
      },
      { property: "og:title", content: "Website Design & Development — Mind Media" },
      {
        property: "og:description",
        content:
          "Custom websites built to convert. Senior-led, fixed pricing, no runaround. See your mockup before you pay a dime.",
      },
      { property: "og:url", content: "https://mindmedia.co/websites" },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [{ rel: "canonical", href: "https://mindmedia.co/websites" }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Service",
          name: "Website Design & Development",
          serviceType: "Website Design and Development",
          provider: { "@type": "Organization", name: "Mind Media", url: "https://mindmedia.co" },
          areaServed: "Worldwide",
          url: "https://mindmedia.co/websites",
          description:
            "Strategy-led website design and development. Fast, modern sites built to turn visitors into leads and sales.",
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: [...faqLeft, ...faqRight].map((f) => ({
            "@type": "Question",
            name: f.question,
            acceptedAnswer: { "@type": "Answer", text: f.answer },
          })),
        }),
      },
    ],
  }),
  loader: ({ context }) => context.queryClient.ensureQueryData(projectsQueryOptions),
  component: WebsitesPage,
});

const gradText =
  "bg-gradient-to-r from-brand-blue via-purple-500 to-pink-500 bg-clip-text text-transparent";

function WebsitesPage() {
  const navigate = useNavigate();
  const goToMockup = () => navigate({ to: "/get-mockup" });

  return (
    <main className="min-h-screen bg-background text-foreground">
      {/* HERO */}
      <section className="relative isolate overflow-hidden bg-background pb-4 text-center">
        <div
          aria-hidden
          className="pointer-events-none absolute left-[5%] bottom-[5%] z-0 h-[420px] w-[420px] sm:left-[8%] sm:bottom-[8%] sm:h-[720px] sm:w-[720px] -translate-x-1/2 rounded-full bg-hero-glow-warm opacity-40 blur-3xl sm:blur-[120px]"
        />
        <div
          aria-hidden
          className="pointer-events-none absolute -right-40 -top-40 z-0 sm:-right-56 sm:-top-56 h-[420px] w-[420px] sm:h-[720px] sm:w-[720px] rounded-full bg-hero-glow-cool opacity-25 blur-3xl sm:blur-[120px]"
        />
        <HeroNav
          logo={<img src={logo} alt="Mind Media" className="h-7 w-auto" />}
          navigation={[
            { label: "Work", href: "#work" },
            { label: "About", href: "#about" },
            { label: "Services", href: "/#pricing" },
            { label: "Pricing", href: "#pricing" },
            { label: "FAQ", href: "#faq" },
            { label: "Contact", href: "#contact" },
          ]}
          ctaButton={{ label: "Book a Meeting", href: "#contact" }}
          mobilePricingHref="#pricing"
        />
        <div className="relative z-10 mx-auto px-6 mt-12 max-w-4xl -translate-y-5 text-left sm:text-center sm:mt-16 lg:max-w-none">
          <HeroSocialProof mobileAlign="left" />
          <h1 className="mx-auto max-w-3xl text-left text-4xl sm:text-center font-bold tracking-tight text-foreground sm:text-5xl lg:max-w-none lg:whitespace-nowrap lg:text-5xl">
            Your website should be&nbsp;<span className={gradText}>making money.</span>
          </h1>
          <p className="mx-auto mt-5 max-w-3xl text-lg text-muted-foreground sm:text-xl">
            We build websites that turn visitors into leads, sales, and booked calls
          </p>
          <div className="mt-8 flex flex-wrap justify-start gap-3 sm:justify-center">
            <button
              onClick={goToMockup}
              className="inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-brand-blue to-[#ec4899] px-6 py-3 text-sm font-semibold text-white shadow-lg shadow-brand-blue/40 transition hover:-translate-y-0.5 hover:opacity-90"
            >
              Get a Free Mockup <ArrowRight className="h-4 w-4" />
            </button>
            <a
              href="#pricing"
              onClick={(e) => {
                e.preventDefault();
                scrollToAnchor("pricing");
              }}
              className="inline-flex items-center gap-2 rounded-full border border-border bg-background px-6 py-3 text-sm font-semibold text-foreground transition hover:-translate-y-0.5 hover:bg-accent"
            >
              Pricing
            </a>
          </div>
          <div className="relative mx-auto mt-8 flex flex-wrap justify-start gap-x-16 sm:justify-center gap-y-4">
            {["Free 30-min Strategy Session", "100% Money Back Guarantee", "No Commitment"].map(
              (p) => (
                <span key={p} className="inline-flex items-center gap-2 text-base font-medium">
                  <span className="flex h-5 w-5 items-center justify-center rounded-full border border-brand-green bg-white">
                    <Check className="h-3 w-3 text-brand-green" strokeWidth={3} />
                  </span>
                  {p}
                </span>
              ),
            )}
          </div>
        </div>

        {/* WHY YOUR WEBSITE SHOULD BE MAKING MONEY */}
        <WhyUsSection
          onCtaClick={goToMockup}
          variant="hero"
          showTrustBar={false}
          showRealExperience={false}
          withDivider={false}
        />
      </section>

      {/* PORTFOLIO — WordPress-driven */}
      <SelectedWork hideTier />

      {/* TIAGO SOUSA TESTIMONIAL */}
      <TestimonialFeatureSection />

      {/* THE PROBLEM & THE REALITY (from homepage) */}
      <ProblemSection onCtaClick={goToMockup} />

      {/* PROBLEM / WHY MOST SITES FAIL */}
      <section className="bg-muted/30 px-6 py-20">
        <div className="mx-auto max-w-6xl">
          <SectionHead
            kickerWeight="font-bold"
            kicker="Why most sites fail"
            maxWidth="max-w-5xl"
            title={
              <>
                A beautiful site no body trusts
                <br className="hidden md:block" /> is just an{" "}
                <span className={gradText}>expensive brochure.</span>
              </>
            }
          >
            Most small business websites are slow, unclear, and not built to convert. The result?
            Low trust, low leads, and lost revenue.
          </SectionHead>
          <div className="grid gap-6 md:grid-cols-3">
            {[
              {
                icon: AlertTriangle,
                h: "Slow, outdated design that instantly erodes trust",
                p: "Visitors judge your business within seconds.",
              },
              {
                icon: MessageSquareOff,
                h: "Weak messaging with no clear offer or next step",
                p: "Confusing sites lose visitors who don't know what you do.",
              },
              {
                icon: Ban,
                h: "No conversion path or poor mobile experience",
                p: "Traffic comes, but leads never happen.",
              },
            ].map(({ icon: Icon, ...c }) => (
              <div key={c.h} className="rounded-2xl border border-border bg-card p-7 shadow-sm">
                <div className="flex items-center gap-4">
                  <div className="inline-flex h-14 w-14 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-brand-blue/15 to-pink-500/15">
                    <Icon className="h-7 w-7 text-brand-blue" strokeWidth={2.25} />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold tracking-tight">{c.h}</h3>
                    <p className="mt-1 text-[15px] text-muted-foreground">{c.p}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* WHAT'S INCLUDED */}
      <section id="included" className="px-6 py-20">
        <div className="mx-auto max-w-6xl">
          <SectionHead
            maxWidth="max-w-4xl"
            kickerWeight="font-bold"
            kicker="What's included"
            title={
              <>
                Everything your website needs, <span className={gradText}>handled.</span>
              </>
            }
          />
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
            {[
              {
                icon: Target,
                h: "Strategy & Messaging",
                b: ["Business goals alignment", "Competitor insights", "Clear messaging & offers"],
              },
              {
                icon: Layout,
                h: "UX / UI Design",
                b: ["User-first layouts", "Modern, on-brand design", "Intuitive experience"],
              },
              {
                icon: Code2,
                h: "WordPress & Shopify Builds",
                b: ["Custom site builds", "Easy to manage", "Scalable & secure"],
              },
              {
                icon: Smartphone,
                h: "Mobile Optimization",
                b: ["Fully responsive design", "Touch-friendly layouts", "Perfect on every device"],
              },
              {
                icon: Gauge,
                h: "Speed & Performance",
                b: [
                  "Lightning-fast load times",
                  "Image & code optimization",
                  "Core Web Vitals ready",
                ],
              },
              {
                icon: MousePointerClick,
                h: "Conversion-Focused Structure",
                b: [
                  "Clear calls-to-action",
                  "Lead generation flows",
                  "Built to turn visitors into leads",
                ],
              },
              {
                icon: Search,
                h: "Basic On-Page SEO",
                b: ["Meta titles & descriptions", "Clean URL structure", "Header & content setup"],
              },
              {
                icon: BarChart3,
                h: "Forms, Tracking & Analytics",
                b: ["Lead capture forms", "Google Analytics setup", "Conversion tracking"],
              },
            ].map(({ icon: Icon, h, b }) => (
              <div
                key={h}
                className="group rounded-2xl border border-border bg-card p-6 transition hover:-translate-y-1 hover:border-brand-blue/30 hover:shadow-xl hover:shadow-brand-blue/10"
              >
                <div className="flex items-center gap-3">
                  <div className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br from-brand-blue/15 to-pink-500/15">
                    <Icon className="h-5 w-5 text-brand-blue" strokeWidth={2.25} />
                  </div>
                  <h3 className="text-base font-bold tracking-tight">{h}</h3>
                </div>
                <ul className="mt-4 space-y-2">
                  {b.map((li) => (
                    <li key={li} className="flex items-start gap-2 text-sm font-medium">
                      <Check
                        className="mt-0.5 h-4 w-4 flex-shrink-0 text-brand-green"
                        strokeWidth={3}
                      />
                      {li}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PROCESS */}
      <section id="process" className="bg-muted/30 px-6 py-20">
        <div className="mx-auto max-w-6xl">
          <SectionHead
            maxWidth="max-w-4xl"
            kickerWeight="font-bold"
            kicker="HOW IT WORKS"
            title={
              <>
                A clear process.
                <br className="md:hidden" /> <span className={gradText}>No mystery.</span>
              </>
            }
          />
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
            {[
              {
                n: 1,
                h: "Audit",
                p: "We review your business, competitors, and current site to find what's holding you back.",
              },
              {
                n: 2,
                h: "Wireframe / Structure",
                p: "We map out the user journey and page structure with purpose.",
              },
              {
                n: 3,
                h: "Design",
                p: "We create a custom, on-brand design that builds trust and drives action.",
              },
              {
                n: 4,
                h: "Build & Refine",
                p: "We build your site, test it across devices, and fine-tune until it's perfect.",
              },
            ].map((s) => (
              <div
                key={s.n}
                className="flex items-center gap-5 rounded-2xl border border-border bg-card p-6"
              >
                <div
                  className={cn(
                    "flex-shrink-0 text-6xl leading-none tracking-tight font-bold text-brand-blue",
                  )}
                >
                  {s.n}
                </div>
                <div>
                  <h3 className="text-base font-bold tracking-tight">{s.h}</h3>
                  <p className="mt-2 text-sm text-muted-foreground">{s.p}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* WHY WORK WITH ME */}
      <section className="pt-12 pb-12 sm:pb-20">
        <AboutSection onCtaClick={goToMockup} />
        <div className="mx-auto max-w-6xl px-6">
          <div className="mx-auto mt-12 max-w-5xl text-center">
            <h2 className="text-3xl font-bold tracking-tight text-balance text-foreground sm:text-5xl lg:text-5xl">
              Senior, strategy-led <br className="md:hidden" /> web design.{" "}
              <span className={gradText}>No runaround.</span>
            </h2>
          </div>
          <div className="mt-16 grid items-stretch gap-12 sm:gap-8 lg:grid-cols-2">
            <ul className="space-y-5">
              {[
                [
                  "You work directly with Wendel, from start to finish.",
                  "No middlemen. No handoffs.",
                ],
                [
                  "Senior-level design with strategy behind every decision.",
                  "Beautiful is great. Profitable is better.",
                ],
                [
                  "Fast communication and quick turnaround.",
                  "You'll always know what's happening.",
                ],
                [
                  "Websites built around your business goals.",
                  "More leads, more calls, more revenue.",
                ],
              ].map(([title, body]) => (
                <li key={title} className="flex gap-4">
                  <span className="mt-1 flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg bg-gradient-to-br from-brand-blue/15 to-pink-500/15">
                    <Check className="h-4 w-4 text-brand-blue" strokeWidth={3} />
                  </span>
                  <div>
                    <strong className="block text-base font-bold">{title}</strong>
                    <span className="text-[15px] text-muted-foreground">{body}</span>
                  </div>
                </li>
              ))}
            </ul>

            <div className="relative rounded-3xl bg-gradient-to-br from-brand-blue via-purple-600 to-pink-500 p-10 text-white shadow-2xl shadow-purple-500/30">
              <div className="absolute right-6 top-6 text-right">
                <div className="text-3xl font-bold leading-none">20+</div>
                <div className="text-xs font-medium uppercase tracking-wider text-white/80">
                  YEARS EXP
                </div>
              </div>
              <div className="text-4xl font-bold tracking-tight leading-tight">
                Built to make your <br className="hidden md:block" /> website pay for itself.
              </div>
              <p className="mt-5 text-[15px] leading-relaxed text-white/90">
                Every design decision is made to increase trust, generate leads, and grow your
                revenue.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* DIVIDER */}
      <div className="mx-auto max-w-7xl px-6 py-8 sm:py-10 lg:px-10">
        <div className="h-px w-full bg-gradient-to-r from-transparent via-foreground/25 to-transparent" />
      </div>

      {/* PRICING */}
      <PricingSection onCtaClick={goToMockup} />

      {/* FAQ */}
      <FaqSection />

      <ContactSection compact />

      <Footer
        logo={<img src={logo} alt="Mind Media" className="h-7 w-auto" />}
        brandName="Mind Media"
        socialLinks={FOOTER_SOCIAL_LINKS}
        mainLinks={[
          { href: "/", label: "Home" },
          { href: "/websites", label: "Websites" },
          { href: "/seo", label: "SEO" },
          { href: "/#pricing", label: "Pricing" },
          { href: "#faq", label: "FAQ" },
          { href: "/blog", label: "Blog" },
          { href: "/services", label: "Services" },
        ]}
        legalLinks={[
          { href: "/privacy", label: "Privacy" },
          { href: "/terms", label: "Terms" },
        ]}
        copyright={{
          text: `© ${new Date().getFullYear()} Mind Media`,
          license: "All rights reserved",
        }}
      />
    </main>
  );
}

function Kicker({ children, className }: { children: React.ReactNode; className?: string }) {
  return (
    <span
      className={cn(
        "inline-block rounded-full bg-brand-blue/10 px-4 py-1.5 text-xs uppercase tracking-[0.12em] text-brand-blue",
        className,
      )}
    >
      {children}
    </span>
  );
}

function SectionHead({
  kicker,
  title,
  children,
  kickerWeight = "font-bold",
  maxWidth = "max-w-2xl",
  titleClassName,
}: {
  kicker: string;
  title: React.ReactNode;
  children?: React.ReactNode;
  kickerWeight?: "font-bold" | "font-medium";
  maxWidth?: string;
  titleClassName?: string;
}) {
  return (
    <div className={cn("mx-auto mb-12 text-center", maxWidth)}>
      <Kicker className={kickerWeight}>{kicker}</Kicker>
      <h2
        className={cn(
          "mt-4 text-4xl font-bold tracking-tight text-foreground sm:text-5xl lg:text-5xl",
          titleClassName,
        )}
      >
        {title}
      </h2>
      {children && <p className="mt-4 text-lg leading-relaxed text-muted-foreground">{children}</p>}
    </div>
  );
}
